import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { PrestoLinkedService } from 'app/model/linkedservice/presto.linkedservice';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { CONNECTOR_VERSION20 } from 'app/services/globals';
import { driverVersions } from 'app/registries/consts/tabular-consts';
import { ITridentResource } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(PrestoLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: '*',
            typeProperties: {
                host: '*',
                serverVersion: '*',
                catalog: '*',
                port: '*',
                authenticationType: '*',
                username: '*',
                password: 'secret',
                enableSsl: '*',
                trustedCertPath: '*',
                useSystemTrustStore: '*',
                allowHostNameCNMismatch: '*',
                allowSelfSignedServerCert: '*',
                timeZoneID: '*',
                encryptedCredential: '*',
                enableServerCertificateValidation: '*'
            }
        }
    }
})
export class PrestoLinkedServiceAdapter extends LinkedServiceAdapter<PrestoLinkedService> {
    protected modelConstructor(): new () => PrestoLinkedService {
        return PrestoLinkedService;
    }

    protected model(): IModel {
        return PrestoLinkedService.MODEL;
    }

    protected override toAzureTypeProperties = (tridentResource: ITridentResource): Model.PrestoLinkedServiceTypeProperties => {
        const datasourceReference: Record<'kind' | 'path', string> = JSON.parse(
            tridentResource.datasourceReference
        );
        const [server, catalog, timeZoneID, enableServerCertificateValidation] = datasourceReference.path.split(';');
        const [host, port] = server.split(':');
        return {
            host,
            port,
            catalog,
            timeZoneID,
            enableServerCertificateValidation,
            authenticationType: tridentResource.credentialDetails.credentialType
        };
    };

    protected async propertiesToResourceAsync(linkedService: PrestoLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.PrestoLinkedService;
        properties.type = Metamodel.modelFor(PrestoLinkedService).name;
        properties.version = linkedService.version === driverVersions.v2 ? CONNECTOR_VERSION20 : undefined;
        const isV2 = linkedService.version === driverVersions.v2;

        properties.typeProperties = {
            host: linkedService.host,
            serverVersion: isV2 ? undefined : linkedService.serverVersion,
            catalog: linkedService.catalog,
            port: linkedService.port,
            authenticationType: linkedService.authenticationType,
            username: linkedService.username,
            password: passwordOrAKVtoSecretBase(linkedService.password, this.context),
            enableSsl: linkedService.enableSsl,
            // v2
            enableServerCertificateValidation: linkedService.enableSsl && isV2 ? linkedService.enableServerCertificateValidation : undefined,
            // v1
            trustedCertPath: isV2 ? undefined : linkedService.enableSsl ? linkedService.trustedCertPath : undefined,
            useSystemTrustStore: isV2 ? undefined : linkedService.enableSsl ? linkedService.useSystemTrustStore : undefined,
            allowHostNameCNMismatch: isV2 ? undefined : linkedService.enableSsl ? linkedService.allowHostNameCNMismatch : undefined,
            allowSelfSignedServerCert: isV2 ? undefined : linkedService.enableSsl ? linkedService.allowSelfSignedServerCert : undefined,
            timeZoneID: linkedService.timeZoneID
        };

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: PrestoLinkedService): Promise<PrestoLinkedService> {
        const properties = linkedServiceResource.properties as Model.PrestoLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.PrestoLinkedServiceTypeProperties = properties.typeProperties;
        linkedService.version = properties.version === CONNECTOR_VERSION20 ? driverVersions.v2 : driverVersions.v1;
        if (__IS_FABRIC_DPE__) {
            linkedService.version = driverVersions.v2;
        }
        const isV2 = linkedService.version === driverVersions.v2;
        linkedService.isLegacyConnector = !isV2;

        linkedService.host = typeProperties.host;
        linkedService.serverVersion = isV2 ? undefined : typeProperties.serverVersion;
        linkedService.catalog = typeProperties.catalog;
        linkedService.port = typeProperties.port;
        linkedService.authenticationType = typeProperties.authenticationType;
        linkedService.username = typeProperties.username;
        linkedService.password = secretBaseToPasswordOrAKV(typeProperties.password, typeProperties.authenticationType === 'LDAP', this.context);
        linkedService.enableSsl = typeProperties.enableSsl;
        // v2
        linkedService.enableServerCertificateValidation = isV2 ? typeProperties.enableServerCertificateValidation : undefined;
        // v1
        linkedService.trustedCertPath = isV2 ? undefined : typeProperties.trustedCertPath;
        linkedService.useSystemTrustStore = isV2 ? undefined : typeProperties.useSystemTrustStore;
        linkedService.allowHostNameCNMismatch = isV2 ? undefined : typeProperties.allowHostNameCNMismatch;
        linkedService.allowSelfSignedServerCert = isV2 ? undefined : typeProperties.allowSelfSignedServerCert;
        linkedService.timeZoneID = typeProperties.timeZoneID;

        return linkedService;
    }
}
